import React, { useState, useRef, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { ChevronDown, Settings, Plus, Briefcase, Check } from 'react-feather';
import useActiveFirm from '../../hooks/useActiveFirm';
import { usePermission } from '../../hooks/usePermission';
import './FirmSwitcher.css';

const FirmSwitcher = () => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const history = useHistory();
    const { userCan } = usePermission();
    
    const { 
        activeFirm, 
        firms, 
        loading, 
        firmsCount,
        setActiveFirm,
        isActiveFirm
    } = useActiveFirm();

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Close dropdown on escape key
    useEffect(() => {
        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                setIsOpen(false);
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, []);

    const handleFirmSelect = (firm) => {
        setActiveFirm(firm);
        setIsOpen(false);
    };

    const handleSettingsClick = (e, firmId) => {
        e.stopPropagation();
        setIsOpen(false);
        history.push(`/firms/${firmId}`);
    };

    const handleAddFirm = () => {
        setIsOpen(false);
        history.push('/firms?action=create');
    };

    const canCreateFirms = userCan('create firms');

    // Don't render if no firms available
    if (!loading && firms.length === 0) {
        return null;
    }

    return (
        <div className="firm-switcher" ref={dropdownRef}>
            <button 
                className={`firm-switcher-button ${isOpen ? 'open' : ''}`}
                onClick={() => setIsOpen(!isOpen)}
                disabled={loading}
            >
                <div className="firm-switcher-icon">
                    <Briefcase size={18} />
                </div>
                <div className="firm-switcher-content">
                    <span className="firm-switcher-name">
                        {loading && !activeFirm ? 'Yuklanmoqda...' : (activeFirm?.name || 'Firma tanlang')}
                    </span>
                </div>
                <div className="firm-switcher-badge">{firmsCount}</div>
                <ChevronDown 
                    size={16} 
                    className={`firm-switcher-chevron ${isOpen ? 'rotated' : ''}`} 
                />
            </button>

            {isOpen && (
                <div className="firm-switcher-dropdown">
                    <div className="firm-switcher-header">
                        Sizning bizneslaringiz ({firmsCount})
                    </div>
                    
                    <div className="firm-switcher-list">
                        {firms.map((firm) => (
                            <div 
                                key={firm.id}
                                className={`firm-switcher-item ${isActiveFirm(firm.id) ? 'active' : ''}`}
                                onClick={() => handleFirmSelect(firm)}
                            >
                                <div className="firm-item-icon">
                                    <Briefcase size={16} />
                                </div>
                                <div className="firm-item-content">
                                    <div className="firm-item-name">
                                        {firm.name}
                                        {isActiveFirm(firm.id) && (
                                            <span className="firm-active-badge">Faol</span>
                                        )}
                                    </div>
                                    {firm.address && (
                                        <div className="firm-item-address">{firm.address}</div>
                                    )}
                                </div>
                                <div className="firm-item-actions">
                                    {isActiveFirm(firm.id) && (
                                        <Check size={16} className="firm-check-icon" />
                                    )}
                                    <button 
                                        className="firm-settings-btn"
                                        onClick={(e) => handleSettingsClick(e, firm.id)}
                                        title="Sozlamalar"
                                    >
                                        <Settings size={14} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>

                    {canCreateFirms && (
                        <div className="firm-switcher-footer">
                            <button 
                                className="firm-add-btn"
                                onClick={handleAddFirm}
                            >
                                <Plus size={16} />
                                <span>Yangi biznes qo'shish</span>
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default FirmSwitcher;
